import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import { telLink, waLink, track } from "../data/products";
import { useLang } from "../i18n";
import { openQuote } from "./QuoteForm";
import { EASE, FadeIn, Magnetic, MaskLine, Micro } from "./Reveal";

export default function ContactSection() {
  const { t } = useLang();
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-24 relative px-5 md:px-12 py-28 md:py-44 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] rounded-full bg-gold/[0.05] blur-[130px] pointer-events-none" />
      <motion.div
        className="absolute top-0 left-0 h-px w-full bg-gold/60 origin-left"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: EASE }}
      />

      <Micro className="text-gold/80">{t("contact.chapter")}</Micro>

      <h2 className="font-display uppercase leading-[0.92] tracking-tight text-[clamp(2.1rem,6.5vw,5.8rem)] mt-8">
        <MaskLine>{t("contact.h1a")}</MaskLine>
        <MaskLine delay={0.1}>{t("contact.h1b")}</MaskLine>
        <MaskLine delay={0.2} innerClass="text-outline-gold">
          {t("contact.h1c")}<span className="text-gold [-webkit-text-stroke:0px]">.</span>
        </MaskLine>
      </h2>

      <FadeIn delay={0.35} className="mt-8">
        <p className="text-sm md:text-base text-cream/55 max-w-md">
          {t("contact.p")}
        </p>
      </FadeIn>

      <FadeIn delay={0.45} className="mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-5">
        <Magnetic className="w-full sm:w-auto">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp"
            data-cursor="link"
            onClick={() => track("contact:whatsapp")}
            className="group inline-flex w-full sm:w-auto justify-center items-center gap-3 bg-gold text-ink px-8 py-4 text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-goldbright transition-colors duration-300"
          >
            {t("contact.whatsapp")}
            <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
          </a>
        </Magnetic>
        <Magnetic className="w-full sm:w-auto">
          <a
            href={telLink}
            data-testid="contact-call"
            data-cursor="link"
            onClick={() => track("contact:phone")}
            className="group inline-flex w-full sm:w-auto justify-center items-center gap-3 border border-white/20 px-8 py-4 text-[11px] font-bold tracking-[0.25em] uppercase text-cream/85 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t("contact.call")}
            <Phone className="w-4 h-4" />
          </a>
        </Magnetic>
        <Magnetic className="w-full sm:w-auto">
          <button
            data-testid="contact-quote"
            data-cursor="link"
            onClick={openQuote}
            className="group inline-flex w-full sm:w-auto justify-center items-center gap-3 border border-gold/50 px-8 py-4 text-[11px] font-bold tracking-[0.25em] uppercase text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            {t("quote.title")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </Magnetic>
      </FadeIn>

    </section>
  );
}
